import React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { format, parseISO, subDays } from 'date-fns';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { Chip, ProgressBar, Row, Text } from 'library/ui';
import { PricingPlan } from 'models';

export interface FeeFreeShiftAllowance {
    business_location_id: number;
    business_location_name: string;
    limit: number;
    used: number;
    scheduled: number;
    remaining: number;
    resets_on: string;
}

export const CLOSE_TO_LIMIT = 2;

export function isOutOfShifts(allowance: FeeFreeShiftAllowance) {
    return allowance.remaining === 0;
}

export function isCloseToLimit(allowance: FeeFreeShiftAllowance) {
    return allowance.used > 0 && allowance.remaining > 0 && allowance.remaining <= CLOSE_TO_LIMIT;
}

export function RemainingLabel({ allowance }: { allowance: FeeFreeShiftAllowance }) {
    if (isOutOfShifts(allowance)) {
        return (
            <Chip size="small" bgColor={Colors.warningBackground} textColor={Colors.warning}>
                0 left — fees apply
            </Chip>
        );
    }

    return (
        <Text size="sm">
            <Text size="sm" weight="bold">
                {allowance.remaining}
            </Text>{' '}
            of {allowance.limit} left
        </Text>
    );
}

export function AllowanceBar({ allowance, compact = false }: { allowance: FeeFreeShiftAllowance; compact?: boolean }) {
    return (
        <BarWrapper $compact={compact}>
            <ProgressBar
                value={allowance.used}
                bufferValue={allowance.used + allowance.scheduled}
                max={allowance.limit}
                color={isOutOfShifts(allowance) ? Colors.warning : Colors.primary}
                bufferColor={Colors.secondaryMuted}
            />
        </BarWrapper>
    );
}

export function ShiftsLeftChip({ allowance }: { allowance: FeeFreeShiftAllowance }) {
    if (isOutOfShifts(allowance)) {
        return (
            <Chip size="small" bgColor={Colors.warningBackground} textColor={Colors.warning}>
                0 left — fees apply
            </Chip>
        );
    }

    return (
        <Chip size="small" bgColor={Colors.secondarySoft} textColor={Colors.secondary}>
            {allowance.remaining} fee-free {allowance.remaining === 1 ? 'shift' : 'shifts'} left
        </Chip>
    );
}

export function serviceFeeDescription(pricingPlan: PricingPlan) {
    return pricingPlan.hourly_type === 'FIXED'
        ? `$${pricingPlan.hourly_value} per hour`
        : `${pricingPlan.hourly_value}% of the substitute's hourly rate`;
}

export function ShiftsLeftNote({
    allowance,
    feeDescription,
}: {
    allowance: FeeFreeShiftAllowance;
    feeDescription?: string;
}) {
    const resets = format(parseISO(allowance.resets_on), 'MMM d');

    if (isOutOfShifts(allowance)) {
        const month = format(subDays(parseISO(allowance.resets_on), 1), 'MMMM');
        return (
            <Row gap="sm" align="start">
                <ErrorOutlineIcon sx={{ fontSize: 16, color: Colors.warning, flexShrink: 0, marginTop: '2px' }} />
                <Text size="sm" tone="warning">
                    Out of fee-free shifts for {month} — Tandem&apos;s administration fee
                    {feeDescription ? ` (${feeDescription})` : ''} applies to new shifts. Resets {resets}.
                </Text>
            </Row>
        );
    }

    if (allowance.scheduled > 0) {
        return (
            <Text size="sm" tone="secondary">
                Counts {allowance.scheduled} scheduled {allowance.scheduled === 1 ? 'shift' : 'shifts'} that
                haven&apos;t been paid out yet — if one cancels, a fee-free shift frees up.
            </Text>
        );
    }

    return null;
}

export function UsageBreakdown({ allowance }: { allowance: FeeFreeShiftAllowance }) {
    return (
        <Text size="sm" tone="secondary">
            {allowance.used} used · {allowance.scheduled} scheduled
        </Text>
    );
}

const BarWrapper = styled('div', { shouldForwardProp: (prop) => prop !== '$compact' })<{ $compact: boolean }>(
    ({ $compact }) => ({
        display: 'flex',
        flex: $compact ? 'none' : 1,
        width: $compact ? 200 : 'auto',
        minWidth: 120,
        maxWidth: '100%',
    }),
);
